import React, { useEffect, useState } from 'react';
import { Box, IconButton, Stack, Typography, CircularProgress } from '@mui/material';
import { CancelOutlined, AccountBalanceWallet } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootStateType } from '@Store';
import Modal from '@Components/Modal';
import SbButton from '@Components/Button';
import SbTextField from '@Components/Textfield';
import API from '@Services/apiAxios';
import { SuccessErrorModalOpen } from '@Reducers/SuccessErrorModal';
import { createPaymentForPurchaseInvoiceCash } from '@Reducers/InvoiceList/actions';
import type { PaymentRow } from './types';
import { PaymentOption } from '../../../Invoice/Payment/Options';

interface PaymentModalProps {
  row: PaymentRow;
  handleSuccess: () => void;
  isAccountHolder: boolean;
  open: boolean;
  onClose: () => void;
}

const PaymentPurchaseInvoiceModal: React.FC<PaymentModalProps> = ({
  row,
  handleSuccess,
  isAccountHolder,
  open,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const defaultOrgCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  const [creditBalance, setCreditBalance] = useState({
    isSelected: false,
    amount: 0,
  });

  const [loading, setLoading] = useState(false);

  const { values, handleChange, errors, handleSubmit, resetForm } = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      cashAmount: 0,
      deductionAmount: 0,
      comment: '',
      invoiceNumber: row.raw.invoiceNumber,
      currency: row.raw.currency || defaultOrgCurrency,
      creditnoteAmount: creditBalance.isSelected
        ? row.raw.invoiceAmount - row.raw.paidAmount - row.raw.deductionPaid >= creditBalance.amount
          ? creditBalance.amount
          : row.raw.invoiceAmount - row.raw.paidAmount - row.raw.deductionPaid
        : 0,
    },
    validationSchema: Yup.object().shape({
      cashAmount: Yup.number()
        .typeError('Amount must be a number')
        .test('decimal', 'Amount can have up to 2 decimal places', v => /^-?\d+(\.\d{1,2})?$/.test(v?.toString() || ''))
        .test('net-payable', 'Total payment cannot exceed due amount', function (v) {
          const cashAmount = v ?? 0;
          const deductionAmount = this.parent.deductionAmount ?? 0;
          const creditnoteAmount = creditBalance.isSelected ? creditBalance.amount : 0;

          const totalPayment = cashAmount + deductionAmount + creditnoteAmount;
          return totalPayment <= row.raw.invoiceAmount - row.raw.paidAmount - row.raw.deductionPaid;
        }),
      deductionAmount: Yup.number()
        .typeError('Deduction must be a number')
        .test('decimal', 'Deduction can have up to 2 decimal places', v =>
          /^-?\d+(\.\d{1,2})?$/.test(v?.toString() || ''),
        ),
      comment: Yup.string()
        .trim()
        .max(255, 'Comment must be at most 255 characters long')
        .required('Comment is required'),
    }),
    async onSubmit(values) {
      setLoading(true);

      try {
        const paymentMode =
          Number(values.cashAmount) > 0 && Number(values.creditnoteAmount) > 0
            ? 'cash+creditnote'
            : Number(values.cashAmount) > 0
            ? 'cash'
            : 'creditnote';

        const totalAmount =
          Number(values.deductionAmount || 0) + Number(values.cashAmount || 0) + Number(values.creditnoteAmount || 0);

        const postData = {
          ...values,
          paymentMode,
          totalAmount,
        };

        await dispatch(
          createPaymentForPurchaseInvoiceCash({
            postData,
            handleSuccess: () => {
              handleSuccess();
              resetForm();
              onClose();
            },
          }),
        );
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      }
    },
  });

  const fetchCreditBalance = async (userId: string) => {
    try {
      const res = await API.get(`/payment/purchase-invoice/credit-note-balance/${userId}`);
      if (res.data?.data?.balance > 0) {
        setCreditBalance({
          isSelected: true,
          amount: res.data?.data?.balance,
        });
      }
    } catch (error: any) {
      dispatch(
        SuccessErrorModalOpen({
          type: 'error',
          title: 'Oops!',
          subTitle: error.response?.data?.msg || 'Something went wrong.',
        }),
      );
    }
  };

  useEffect(() => {
    if (open) {
      fetchCreditBalance(row.raw.carrier._id);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      maxWidth="lg"
      sx={{
        padding: '2rem',
        '@media (max-width: 600px)': { padding: '1rem' },
      }}
    >
      <Box
        width={'28rem'}
        sx={{
          '@media (max-width: 600px)': { width: '100%' },
        }}
      >
        <Stack gap={'1rem'}>
          <Stack direction="row-reverse">
            <IconButton onClick={onClose}>
              <CancelOutlined sx={{ fontSize: '2rem' }} />
            </IconButton>
          </Stack>

          {isAccountHolder && (
            <PaymentOption
              isRadio={false}
              title="Credit Note"
              subTitle={`Available balance: ${defaultOrgCurrency} ${creditBalance.amount || 0}`}
              icon={<AccountBalanceWallet sx={{ height: 40, width: 40, color: 'primary.main' }} />}
              selectedAmount={creditBalance}
              onSelect={obj => setCreditBalance(obj)}
              amount={creditBalance.amount}
              isDisabled={creditBalance.amount <= 0}
              currency={defaultOrgCurrency}
            />
          )}

          <Typography variant="subtitle1" fontSize="20px">
            Pay by Cash
          </Typography>

          <SbTextField
            label="Amount Paid"
            required
            name="cashAmount"
            value={values.cashAmount}
            onChange={handleChange}
            disabled={
              row.raw.invoiceAmount - row.raw.paidAmount - row.raw.deductionPaid <= creditBalance.amount &&
              creditBalance.isSelected
            }
            error={errors.cashAmount}
            // type="number"
          />

          {isAccountHolder && (
            <SbTextField
              label="Deductions"
              name="deductionAmount"
              value={values.deductionAmount || 0}
              onChange={handleChange}
              disabled={
                row.raw.invoiceAmount - row.raw.paidAmount - row.raw.deductionPaid <= creditBalance.amount &&
                creditBalance.isSelected
              }
              error={errors.deductionAmount}
              // type="number"
            />
          )}

          <SbTextField
            label="Comment"
            name="comment"
            required
            value={values.comment}
            onChange={handleChange}
            error={errors.comment}
            placeholder="Paid By RTGS UTR NO 1234"
          />

          <Typography fontWeight={700} fontSize="16px">
            Total Amount Paid:{' '}
            {(() => {
              const total =
                Number(values.cashAmount || 0) +
                Number(values.deductionAmount || 0) +
                (creditBalance.isSelected ? Number(creditBalance.amount || 0) : 0);

              return !isNaN(total) ? `${total} ${row.raw.currency || defaultOrgCurrency}` : '-';
            })()}
          </Typography>

          <SbButton
            variant="contained"
            onClick={() => handleSubmit()}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {loading ? 'Processing...' : 'Update'}
          </SbButton>
        </Stack>
      </Box>
    </Modal>
  );
};

export default PaymentPurchaseInvoiceModal;
